import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO, isBefore } from "date-fns";
import { supabase } from "../../lib/supabase";
import { 
  Paperclip, 
  AlertTriangle, 
  Check, 
  X, 
  Upload,
  ChevronDown,
  CheckCircle2,
  Search,
  File,
  Cog
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
// import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { useToast } from "../../hooks/use-toast";
import { ApiCalls, emptyResponseData, IReceiptData, IReceiptsResponseData, IReimbursementPackageData, IReimbursementPackagesResponseData, IReimbursementUsersSummaryData, IReimbursementUsersSummaryResponseData } from "../../ApiCalls";
import { selectedUserState } from "../../redux/slices/userSlice";
import { useSelector } from "react-redux";
import { ReduxUpdater } from "../../redux/ReduxUpdater";
import { ReceiptPreview } from "./elements/ReceiptPreview";
import { GlobalConstants } from "../../GlobalConstants";
import { trimStringLength } from "./Expenses";
import { selectedYachtState } from "../../redux/slices/yachtSlice";
import PopupMenu, { IPopupMenuAction } from "./elements/PopupMenu";
import { toast as reactToastify } from "react-toastify";
import { GlobalVariables } from "../../GlobalVariables";
import { formatReimbursementDate } from "./elements/Forms/DateTimeInput";
import Spinner from "./icons/Spinner";
import Tooltip from "./tooltiped/Tooltip";
import { GlobalPermissions } from "../../GlobalPermissions";
import { Column, DataTable } from "./elements/Forms/DataTable";
import { DualSelect, DualSelectTheme } from "./elements/Forms/DualSelect";

type UsersSummaryType = {[id: number]: IReimbursementUsersSummaryData};

// --- Main Component ---
export default function ReimbursementManager() {
    const currentSelectedUserState = useSelector(selectedUserState);
    const currentSelectedYachtState = useSelector(selectedYachtState);
    
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(100);
    const [receiptData, setReceiptData] = useState<IReceiptsResponseData>(emptyResponseData);
    const [reimbursementUsersSummaryData, setReimbursementUsersSummaryData] = useState<UsersSummaryType>({});
    const [lastTS, setLastTS] = useState(Date.now());

    const [role, setRole] = useState<'user' | 'manager'>(ReduxUpdater.authenticatedUser?.user_role === GlobalConstants.UserRole.ROOT ? 'manager' : 'user');
    // const [selectedUserFilter, setSelectedUserFilter] = useState<number>(-1);
    const [activeTab, setActiveTab] = useState<'pending' | 'reimbursed'>('pending');
    const [searchQuery, setSearchQuery] = useState('');
    // const [sortConfig, setSortConfig] = useState<{ key: 'date' | 'amount', direction: 'asc' | 'desc' }>({ key: 'date', direction: 'desc' });

    const [ImageURL, setImageURL] = React.useState(ReduxUpdater.offCanvas?.supabase_image_link ?? '');
    // const [PdfURL, setPdfURL] = React.useState(ReduxUpdater.offCanvas?.supabase_image_link ?? '');
    const [SignedImageURL, setSignedImageURL] = React.useState('');
    const [documentType, setDocumentType] = React.useState<'image' | 'pdf'>('image');

    React.useEffect(() => {
        console.log('setImageURL:', ImageURL);
        if (ImageURL === null || ImageURL === '') {
            ReduxUpdater.documentLoadingError = true;
            return;
        }
        console.log('ImageURL:', ImageURL);
        ReduxUpdater.documentLoading = true;
        ApiCalls.getSignedUrl(ImageURL).then(url => {
            console.log('setSignedImageURL:', url);
            setDocumentType(ImageURL.endsWith('.pdf') ? 'pdf' : 'image');
            setSignedImageURL(url);
            // ReduxUpdater.imageLoading = false;
        });
    }, [ImageURL]);

    // const handleSort = (key: 'date' | 'amount') => {
    //     setSortConfig(current => ({
    //         key,
    //         direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
    //     }));
    // };
    
    const { toast } = useToast();
    const queryClient = useQueryClient();

    // accountingCategories, coOwners
    React.useEffect(() => {
        if (!currentSelectedYachtState) {
            GlobalVariables.accountingCategories = [];
            return;
        }
        ApiCalls.accountingCategories().then(d => { GlobalVariables.accountingCategories = [...d] }).catch(err => console.error(err));
        ApiCalls.coOwners(currentSelectedYachtState.id).then(d => { GlobalVariables.coOwners = d.items }).catch(err => console.error(err));
        ApiCalls.reimbursementUsersSummary(currentSelectedYachtState.id).then(d => {
            const userSummary: UsersSummaryType = {};
            d.items.forEach(o => {
                userSummary[o.user_id] = o;
            });
            setReimbursementUsersSummaryData(userSummary);
        }).catch(err => console.error(err));
        queryClient.invalidateQueries({ queryKey: ['receipts'] });
    }, [currentSelectedYachtState, queryClient]);

    // Fetch receipts
    const { data: receipts = [], isLoading, error, refetch } = useQuery({
        queryKey: ['receipts', currentSelectedUserState],
        queryFn: async () => {
            console.log('useQUERY!!!', currentSelectedUserState?.id);

            if (!currentSelectedUserState) {
                return [];
            }

            const res = await ApiCalls.reimbursements([currentSelectedUserState.id], currentPage, currentPageSize);
            setReceiptData(res);
            return res.items;
        }
    });

    React.useEffect(() => {
        refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, currentPageSize]);

    // Derived state
    const visibleReceipts = receipts.filter(r => {
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            return r.vendor_name.toLowerCase().includes(q) || r.user_description?.toLowerCase().includes(q);
        }
        return true;
    });

      // Eligible for reimbursement: Approved or Submitted, from previous months, filtered by user if manager
    const eligibleForReimbursement = receipts.filter(r => {
        if (r.reimbursement_package_id !== null) return false;

        const upTo20 = new Date();
        upTo20.setDate(20); // Everything before 20th of the month

        const isPreviousMonth = isBefore(parseISO(r.transaction_date ?? r.created_at), upTo20);
        return isPreviousMonth;
    });

    const eligibleForReimbursementCurrencies = Array.from(new Set(eligibleForReimbursement.map(o => o.currency)));

     // Modals state
    const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
    const [editingReceipt, setEditingReceipt] = useState<Partial<IReceiptData> | null>(null);
    
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [viewingReceipt, setViewingReceipt] = useState<IReceiptData | null>(null);
    const [viewingReport, setViewingReport] = useState<IReimbursementPackageData | null>(null);

    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

    const [reimbursedGroupsData, setReimbursedGroupsData] = useState<IReimbursementPackagesResponseData | null>(null);
    const [isRunning, setIsRunning] = useState(-1);
    
    React.useEffect(() => {
        if (!isViewerOpen) {
            setViewingReceipt(null);
            setViewingReport(null);
        }
    }, [isViewerOpen]);

    React.useEffect(() => {
        if (viewingReceipt) {
            setImageURL(viewingReceipt.supabase_image_link);
        }
        if (viewingReport) {
            setImageURL(viewingReport.report_url);
        }
    }, [viewingReceipt, viewingReport]);

    const toggleGroup = (groupKey: string) => {
        setExpandedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupKey)) newSet.delete(groupKey);
            else newSet.add(groupKey);
            return newSet;
        });
    };
    const [isConfirmReimburseOpen, setIsConfirmReimburseOpen] = useState(false);

    const batchApproveMutation = useMutation({
        mutationFn: async () => {
            const toApprove = visibleReceipts.filter(r => r.receipt_status === 'submitted');
            
            if (toApprove.length === 0) return;
            
            const { error } = await supabase.from('receipts').update({ status: 'Approved' }).in('id', toApprove.map(r => r.id));
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['receipts'] });
            toast({ title: "Batch Approved", description: "Pending receipts have been approved." });
        }
    });

    const initiateReimbursementMutation = useMutation({
        mutationFn: async () => {
            if (!currentSelectedUserState) {
                return;
            }
            const res = await ApiCalls.generateReimbursementReport(currentSelectedUserState.id);
            if (res) {
                GlobalVariables.handleDownload(res, `${currentSelectedYachtState?.name}_reimbursement_${currentSelectedUserState?.first_name}_${currentSelectedUserState?.last_name}.zip`, 'application/zip');
                queryClient.invalidateQueries({ queryKey: ['receipts'] });
                setLastTS(Date.now());
                reactToastify.success('Report generated');
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['receipts'] });
                setIsConfirmReimburseOpen(false);
                toast({ title: "Reimbursement Initiated", description: "All eligible receipts have been processed." });
        }
    });

    React.useEffect(() => {
        if (!currentSelectedUserState) {
            setReimbursedGroupsData(null);
            return;
        }

        ApiCalls.reimbursementReports([currentSelectedUserState.id], 1).then(res => setReimbursedGroupsData({...res})).catch(err => console.error(err));
    }, [currentSelectedUserState, lastTS]);

    // Group Reimbursed receipts by event
    const reimbursedGroups = React.useMemo(() => {
        console.log('reimbursedGroups', reimbursedGroupsData);
        if (!reimbursedGroupsData) {
            return [];
        }

        return reimbursedGroupsData.items.map(d => {
            const receipts = d.receipts;

            if (!receipts) {
                return null;
            }

            const dates = receipts.map(r => new Date(r.transaction_date ?? r.created_at).getTime());
            const minDate = new Date(Math.min(...dates));
            const maxDate = new Date(Math.max(...dates));
            const date = new Date(d.package_date ?? d.created_at);
            const monthName = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date);
            
            const dateRange = `Date range: ${new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short', year: 'numeric' }).format(minDate).toLowerCase().replace(/,/g,'')} - ${new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short', year: 'numeric' }).format(maxDate).toLowerCase().replace(/,/g,'')}`;

            return {
                key: d.report_id ?? `KEY-${d.id}`,
                title: `Reimbursement for ${monthName}`,
                dateRange,
                report: d,
                // receipts: receipts.sort((a, b) => new Date(b.transaction_date ?? b.created_at).getTime() - new Date(a.transaction_date ?? a.created_at).getTime())
            };
        }).filter(o => o !== null);
    }, [reimbursedGroupsData]);


    const dropdownOptions = React.useMemo(() => {
        return ReduxUpdater
            .yachtCrew
            .filter(o => activeTab === 'pending'
                ? reimbursementUsersSummaryData[o.id] && reimbursementUsersSummaryData[o.id].pending_receipts_count > 0
                : reimbursementUsersSummaryData[o.id] && reimbursementUsersSummaryData[o.id].reimbursement_packages_count > 0
            )
            .sort(GlobalConstants.SORT.compareFnCrew)
            .map((c, i) => ({
                id: c.id,
                label: `${c.first_name} ${c.last_name}`,
                meta: c.position,
                value: c.id.toString(),
                sort: i
            }));
    }, [activeTab, reimbursementUsersSummaryData]);

    React.useMemo(() => {
        console.log('dropdownSelectedOption', dropdownOptions, currentSelectedUserState);
        if (dropdownOptions.length === 0) {
            return;
        }

        console.log('dropdownSelectedOption', currentSelectedUserState && !dropdownOptions.map(o => o.id).includes(currentSelectedUserState.id), dropdownOptions[0].id !== currentSelectedUserState?.id);

        if (
            (currentSelectedUserState && !dropdownOptions.map(o => o.id).includes(currentSelectedUserState.id)) &&
            dropdownOptions[0].id !== currentSelectedUserState.id
        ) {
            const user = ReduxUpdater.yachtCrew.find(c => c.id === dropdownOptions[0].id);
            console.log('dropdownSelectedOption.user', user);
            ReduxUpdater.selectedUser = user ?? null;
        }
    }, [currentSelectedUserState, dropdownOptions]);

    // if (isLoading) return <>Loading({currentSelectedUserState?.id})...</>;
    if (error) return <>An error has occurred: {error.message}</>;

    const ReimbursementsAdminMenu = (receipt: IReceiptData) => {
        if (ReduxUpdater.authenticatedUser?.user_role !== GlobalConstants.UserRole.ROOT) {
            return null;
        }

        const menu: IPopupMenuAction[] = [
        ];
        if (receipt.matched_bank_transaction_id === null) {
            menu.push({
                title: 'Delete',
                extraClass: 'bg-red-600 text-white',
                onClick: () => {
                    const saveReceiptChanges = {
                        id: receipt.id,
                        message_id: receipt.message_id,
                        receipt_status: 'trashed',
                    } as IReceiptData;
                    ApiCalls.updateReceipt(saveReceiptChanges).then(res => {
                        if (res !== null) {
                            queryClient.invalidateQueries({ queryKey: ['receipts'] });
                            reactToastify.success('Receipt Deleted');
                        } else {
                            reactToastify.error('Failed to delete receipt.');
                        }
                    }).catch(err => {
                        console.error(err);
                        reactToastify.error('Failed to delete receipt.');
                    });
                }
            });
            menu.push({
                title: 'DIV',
                onClick: () => {}
            });
        }
        if (receipt.receipt_type === 'reimbursement') {
            // menu.push({
            //     title: 'DIV',
            //     onClick: () => {}
            // });
            if (receipt.reimbursement_package_id === null) {
                menu.push({
                    title: 'Convert to Boat Cash',
                    onClick: () => {
                        const saveReceiptChanges = {
                            id: receipt.id,
                            message_id: receipt.message_id,
                            receipt_status: null,
                            receipt_type: 'petty_cash',
                        } as IReceiptData;
                        ApiCalls.updateReceipt(saveReceiptChanges).then(res => {
                            if (res !== null) {
                                ReduxUpdater.reloadExpensesData();
                                reactToastify.success('Receipt changed to Boat Cash');
                            } else {
                                reactToastify.error('Failed to change receipt.');
                            }
                        }).catch(err => {
                            console.error(err);
                            reactToastify.error('Failed to change receipt.');
                        });
                    }
                });
            }
        }

        return (
            <PopupMenu
                className="ml-1 inline h-8 w-8"
                placement="bottom-end"
                menuElements={menu}
            >
                <Cog
                    className="clickable p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-full text-gray-500 transition-colors bg-white dark:bg-zinc-900 shadow-sm border border-gray-200 dark:border-zinc-800"
                    height={34}
                    width={34}
                />
            </PopupMenu>
        );
    }

    const bgTrigger = 'rgb(226 232 240)';
    const colTrigger = 'black';

    const ddTheme: Partial<DualSelectTheme> = {
        bg: {
            trigger:        bgTrigger,
            dropdown:       "white",
            optionHover:    "#f5f5f5",
            optionSelected: "#efefef",
        },
        text: {
            label:               "#999999",
            placeholder:         "#bbbbbb",
            triggerValue:        colTrigger,
            triggerMeta:         colTrigger,
            optionLabel:         "#333333",
            optionLabelSelected: "#000000",
            optionMeta:          "#999999",
            optionDesc:          "#bbbbbb",
            chevron:             colTrigger,
            selectedDot:         "#111111",
        },
        font: {
            family:       "'DM Mono', 'Fira Mono', 'Courier New', monospace",
            sizeLabel:    "11px",
            sizeTrigger:  "14px",
            sizeMeta:     "14px",
            sizeDesc:     "11px",
        },
        border: {
            trigger:         bgTrigger,
            triggerOpen:     "transparent",
            triggerFocusRing:"!none", //"rgba(0,0,0,0.08)",
            dropdown:        "#e0e0e0",
            divider:         "white",
        },
        dropdownShadow: "!none", //"0 8px 24px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.06)",
        height: {
            dropdown: "auto",
            trigger: "32px",
            divider: "1px",
        },
        radius: {
            dropdown: "10px",
            trigger: "10px",
            option: "10px",
        }
    };


    const COLUMNS: Column<IReceiptData>[] = [
        {
            key: "transaction_date", label: "Date",
            width: "1fr",
            sortable: false,
            render: (_, row) => (<>{formatReimbursementDate(new Date(row.transaction_date ?? row.created_at))}</>),
        },
        {
            key: "amount",
            label: "Amount",
            width: "1fr",
            align: "center",
            sortable: false,
            className: "font-medium text-gray-900 dark:text-gray-100",
            render: (_, row) => (<>{Number(row.amount).toFixed(2)} <span className="text-gray-500 text-xs ml-0.5">{row.currency}</span></>),
        },
        {
            key: "vendor_name", label: "Merchant",
            width: "2fr",
            sortable: false,
            className: "font-medium text-gray-900 dark:text-gray-100",
            render: (_, row) => (<>{trimStringLength(row.vendor_name)}</>),
        },
        {
            key: "user_description",
            label: "Description",
            width: "2fr",
            sortable: false,
            // render: (_, row) => (<>{row.maris_category_id !== null ? trimStringLength(GlobalVariables.accountingCategoriesByID[row.maris_category_id], 30) : ''}</>),
        },
        {
            key: "category",
            label: "Category",
            width: "2fr",
            sortable: false,
            render: (_, row) => (<>{row.maris_category_id !== null ? trimStringLength(GlobalVariables.accountingCategoriesByID[row.maris_category_id], 30) : ''}</>),
        },
        {
            key: "attachment",
            label: 'Attachment',
            width: "1fr",
            align: "center",
            render: (_, row, i) => (
                <>
                    {row.supabase_image_link && <button onClick={() => { setViewingReceipt(row); setIsViewerOpen(true); }} className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-full text-gray-500 transition-colors bg-white dark:bg-zinc-900 shadow-sm border border-gray-200 dark:border-zinc-800">
                            <Paperclip className="w-4 h-4" />
                        </button>}
                    {ReimbursementsAdminMenu(row)}
                </>
            ),
        },
    ];

    return (
        <>
            <div className="w-[1000px] self-stretch mt-2 relative inline-flex flex-col justify-start items-start">
                <div className="w-[1000px] px-4 flex flex-col justify-center items-start gap-6">
                    <div className="self-stretch inline-flex justify-start items-center gap-2">
                        <div className="w-64 h-12 relative">
                            <div className="w-[24rem] left-0 top-0 absolute justify-start text-Text-Default-Default text-2xl font-semibold font-['Inter'] leading-7">Expense Reimbursements</div>
                            <div className="w-44 left-0 top-[33px] absolute justify-start text-text-lighter text-base font-normal font-['Inter'] leading-4">{currentSelectedYachtState?.name}</div>
                        </div>
                        <div className="flex-1 flex justify-end items-center gap-2.5 self-stretch">&nbsp;</div>
                        <div className="max-w-50 flex-1 flex justify-end items-center gap-2.5">
                            {ReduxUpdater.authenticatedUser?.email === 'david.lednik@gmail.com' && <div className="flex gap-1 bg-gray-100/80 dark:bg-zinc-800/80 p-1 rounded-lg shrink-0">
                                <button 
                                    onClick={() => setRole('user')}
                                    className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${role === 'user' ? 'bg-white dark:bg-zinc-700 text-gray-900 dark:text-gray-100 shadow-sm' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                                >
                                    User
                                </button>
                                <button 
                                    onClick={() => setRole('manager')}
                                    className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${role === 'manager' ? 'bg-white dark:bg-zinc-700 text-gray-900 dark:text-gray-100 shadow-sm' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                                >
                                    Manager
                                </button>
                            </div>}
                        </div>
                    </div>
                </div>
            </div>
        
            <div className="w-[1000px] flex h-[-webkit-fill-available] bg-gray-50/50 dark:bg-zinc-950 font-sans">
                <div className="flex-1 flex flex-col self-stretch overflow-hidden">
                {/* Header Bar */}
                <header className="h-16 border-b border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex items-center justify-between px-6 shrink-0">
                    <div className="flex items-center gap-3">
                        &nbsp;
                        {/* <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/80 to-primary text-white flex items-center justify-center font-semibold text-sm shadow-sm ring-2 ring-white dark:ring-zinc-900">
                            {currentSelectedUserState?.first_name.charAt(0)}
                        </div>
                        <div className="hidden md:flex flex-col">
                            <span className="text-sm font-semibold leading-none">{currentSelectedUserState?.first_name} {currentSelectedUserState?.last_name}</span>
                            <span className="text-[10px] text-gray-500 mt-0.5">{role === 'manager' ? 'Finance Manager' : 'Employee'}</span>
                        </div> */}
                    </div>
                    
                    <div className="flex items-center gap-6">
                        { GlobalPermissions.UX.REIMBURSEMENTS.Pending.ChangeUser.includes(ReduxUpdater.authenticatedUser?.user_role ?? '') && <div className="w-[500px] flex items-center gap-2 shrink-0">
                            <span className="text-sm text-gray-500 font-medium hidden sm:inline">View:</span>
                            <DualSelect
                                options={dropdownOptions}
                                value={(currentSelectedUserState?.id ?? -1).toString()}
                                // disabled={(selectedNav === 3 || [GlobalConstants.UserRole.STANDARD, GlobalConstants.UserRole.BASIC].includes(ReduxUpdater.authenticatedUser?.user_role ?? ''))}
                                onChange={(newID) => {
                                    if (parseInt(newID) !== currentSelectedUserState?.id) {
                                        console.log('Reimbursements Card changed...', newID);
                                        ReduxUpdater.selectedUser = ReduxUpdater.yachtCrew.find(c => c.id === parseInt(newID)) ?? null;
                                        queryClient.invalidateQueries({ queryKey: ['receipts'] });
                                    }
                                }}
                                theme={ddTheme}
                            /> 
                        </div>}
                    </div>
                </header>

                {/* Tab Bar & Filter & Totals */}
                <div className="flex items-center justify-between px-6 py-4 bg-white/50 dark:bg-zinc-900/50 border-b border-gray-200 dark:border-zinc-800 shrink-0">
                    {/* Left side: Tabs */}
                    <div className="flex gap-1 bg-gray-100/80 dark:bg-zinc-800/80 p-1 rounded-lg shrink-0">
                        <button 
                            onClick={() => setActiveTab('pending')}
                            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${activeTab === 'pending' ? 'bg-white dark:bg-zinc-700 text-gray-900 dark:text-gray-100 shadow-sm' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                        >
                            Pending
                        </button>
                        <button 
                            onClick={() => setActiveTab('reimbursed')}
                            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${activeTab === 'reimbursed' ? 'bg-white dark:bg-zinc-700 text-gray-900 dark:text-gray-100 shadow-sm' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                        >
                            Reimbursed
                        </button>
                    </div>

                    {/* Right side: Totals + Actions */}
                    <div className="flex items-center gap-4 overflow-x-auto hide-scrollbar pl-4">
                        {role === 'manager' && activeTab === 'pending' && visibleReceipts.filter(r => r.receipt_status === 'submitted').length > 0 && (
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => batchApproveMutation.mutate()}
                                disabled={batchApproveMutation.isPending}
                                className="gap-2 border-primary/20 text-primary hover:bg-primary/5 shrink-0 whitespace-nowrap"
                            >
                                <CheckCircle2 className="w-4 h-4" />
                                Batch Approve All Pending
                            </Button>
                        )}

                        <div className="relative flex items-center">
                            <Search className="w-4 h-4 text-gray-400 absolute left-3" />
                            <Input 
                                placeholder="Search expenses..." 
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="pl-9 h-9 w-[160px] sm:w-[200px] bg-white/50 dark:bg-zinc-900/50 shadow-sm border-gray-200 dark:border-zinc-800 focus-visible:ring-primary/20 transition-all focus:w-[180px] sm:focus:w-[240px]"
                            />
                            {searchQuery && (
                                <button 
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-2 p-1 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            )}
                        </div>

                        {role === 'manager' && activeTab === 'pending' && (
                            <Button 
                                onClick={() => setIsConfirmReimburseOpen(true)}
                                disabled={eligibleForReimbursement.length === 0}
                                className="gap-2 shadow-sm hover:shadow h-9"
                            >
                                Initiate Reimbursement
                            </Button>
                        )}
                    </div>
                </div>

                {/* Pending Totals Section */}
                {activeTab === 'pending' && (
                    <div className="px-6 py-3 bg-gray-50/80 dark:bg-zinc-900/80 border-b border-gray-200 dark:border-zinc-800 shrink-0 flex items-center justify-between">
                        <div className="flex gap-3">
                            <Tooltip text={visibleReceipts.length === 0 ? 'No reimbursements to create preliminary report' : 'Create Preliminary report'}>
                                <button 
                                    disabled={isRunning !== -1 || visibleReceipts.length === 0}
                                    onClick={
                                        (e) => {
                                            e.stopPropagation();
                                            if (!currentSelectedUserState) {
                                                return;
                                            }
                                            setIsRunning(0);
                                            ApiCalls.generatePreliminaryReimbursementReport(currentSelectedUserState.id).then(res => {
                                                if (res) {
                                                    GlobalVariables.handleDownload(res, `${currentSelectedYachtState?.name}_reimbursement_${currentSelectedUserState?.first_name}_${currentSelectedUserState?.last_name}_preliminary.zip`, 'application/zip');
                                                    queryClient.invalidateQueries({ queryKey: ['receipts'] });
                                                    setLastTS(Date.now());
                                                    reactToastify.success('Preliminary report generated');
                                                }
                                                setIsRunning(-1);
                                            }).catch(err => console.error(err));
                                        }
                                    }
                                    className="p-1.5 mr-4 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-full text-gray-500 transition-colors bg-white dark:bg-zinc-900 shadow-sm border border-gray-200 dark:border-zinc-800"
                                >
                                    {isRunning === 0
                                        ? <Spinner height="14" width="14" />
                                        : <File className="w-3.5 h-3.5" />
                                    }
                                </button>
                            </Tooltip>
                            <span className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-widest mr-4">Pending Totals</span>
                        </div>
                        <div className="flex gap-3">
                            {eligibleForReimbursement.length === 0
                                ? <span className="text-sm text-gray-400 italic py-1">No pending amounts</span>
                                : eligibleForReimbursementCurrencies.map(curr => (
                                    <div className="flex flex-col items-end">
                                        <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400 leading-none mb-1">{curr}</span>
                                        <span className="font-mono text-sm font-semibold text-gray-900 dark:text-gray-100">{eligibleForReimbursement.filter(o => o.currency === curr).reduce((a, c) => a + parseFloat(c.amount), 0).toFixed(2)}</span>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                )}

                {/* Content Area */}
                <div className="flex-1 px-6 py-1" style={{ maxHeight: 'calc( 100vh - 334px )' }}>
                    <>
                        {activeTab === 'pending' ? (
                            <DataTable<IReceiptData>
                                columns={COLUMNS}
                                data={visibleReceipts}
                                pagination={{ page: currentPage, pageSize: currentPageSize, total: -1 }}
                                loading={isLoading}
                                onPageChange={setCurrentPage}
                                onPageSizeChange={setCurrentPageSize}
                                className='h-full w-full mb-4 min-h-[400px]'
                                rowCss={'h-12 min-h-12'}
                            />
                        ) : (
                            <div className="space-y-8">
                                {reimbursedGroups.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 border-dashed text-gray-500">
                                        <div className="w-20 h-20 bg-gray-50/80 dark:bg-zinc-800/50 rounded-full flex items-center justify-center mb-5 shadow-sm border border-gray-100 dark:border-zinc-800/80">
                                            <CheckCircle2 className="w-8 h-8 text-gray-400" />
                                        </div>
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No past reimbursements</h3>
                                        <p className="text-sm text-gray-500 leading-relaxed max-w-sm text-center">
                                            Once expenses are approved and processed at the end of the month, they will appear here.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {reimbursedGroups.map(group => {
                                            if (!group) {
                                                return null;
                                            }

                                            const isExpanded = expandedGroups.has(group.key);
                                            return (
                                                <div key={group.key} className="bg-white dark:bg-zinc-900 rounded-xl shadow-sm border border-gray-200
                                                 dark:border-zinc-800 overflow-hidden">
                                                    <div 
                                                        className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50/50 dark:hover:bg-zinc-800/50 transition-colors"
                                                        onClick={() => toggleGroup(group.key)}
                                                    >
                                                        <div className="flex items-center gap-6">
                                                            {role === 'manager' ? <div className="inline-flex justify-start">
                                                                <button 
                                                                    disabled={isRunning !== -1}
                                                                    onClick={
                                                                        (e) => {
                                                                            e.stopPropagation();
                                                                            setIsRunning(group.report.id);
                                                                            ApiCalls.regenerateReimbursementReport(group.report.id).then(res => {
                                                                                if (res) {
                                                                                    GlobalVariables.handleDownload(res, `${currentSelectedYachtState?.name}_reimbursement_${currentSelectedUserState?.first_name}_${currentSelectedUserState?.last_name}_${group.report.package_date}_package_${group.report.id}.zip`, 'application/zip');
                                                                                }
                                                                                reactToastify.success('Report downloaded');
                                                                                setIsRunning(-1);
                                                                            }).catch(err => {
                                                                                setIsRunning(-1);
                                                                                console.error(err);
                                                                            });
                                                                        }
                                                                    }
                                                                    className="p-1.5 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-full text-gray-500 transition-colors bg-white dark:bg-zinc-900 shadow-sm border border-gray-200 dark:border-zinc-800"
                                                                >
                                                                    {isRunning === group.report.id
                                                                        ? <Spinner height="14" width="14" />
                                                                        : <File className="w-3.5 h-3.5" />
                                                                    }
                                                                </button>
                                                            </div> : <></>}
                                                            <div className="inline-block">
                                                                <h3 className="block text-lg font-semibold text-gray-900 dark:text-gray-100">{group.title}</h3>
                                                                <p className="block text-sm text-gray-500 mt-1">{group.dateRange}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-6">
                                                            <div className="hidden md:flex gap-3">
                                                                {(() => {
                                                                const totals = group.report.receipts.reduce((acc, r) => {
                                                                    acc[r.currency] = (acc[r.currency] || 0) + Number(r.amount);
                                                                    return acc;
                                                                }, {} as Record<string, number>);
                                                                return Object.entries(totals).map(([curr, amount]) => (
                                                                    <div key={curr} className="flex flex-col items-end">
                                                                        <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400 leading-none mb-1">{curr}</span>
                                                                        <span className="font-mono text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                                            {curr === 'USD' ? '\u0024' : curr === 'EUR' ? '\u20AC' : '\u00A3'}{amount.toFixed(2)}
                                                                        </span>
                                                                    </div>
                                                                ));
                                                                })()}
                                                            </div>
                                                            <div className="h-8 w-px bg-gray-200 dark:bg-zinc-800 hidden md:block" />
                                                            <div className="flex items-center gap-4">
                                                                <Badge variant="secondary" className="bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-gray-400 font-mono">
                                                                    {group.report.receipts.length} receipt{group.report.receipts.length !== 1 ? 's' : ''}
                                                                </Badge>
                                                                <button className="p-1 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                                                                    {isExpanded ? <ChevronDown className="w-5 h-5 rotate-180 transition-transform" /> : <ChevronDown className="w-5 h-5 transition-transform" />}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                
                                                    {isExpanded && (
                                                        <div className="border-t border-gray-200 dark:border-zinc-800 bg-gray-50/30 dark:bg-zinc-900/30">
                                                            <table className="w-full text-sm text-left">
                                                                <thead className="bg-gray-50/80 dark:bg-zinc-800/50 text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-zinc-800">
                                                                    <tr>
                                                                    <th className="px-6 py-3 font-medium uppercase tracking-wider text-xs">Date</th>
                                                                    <th className="px-6 py-3 font-medium uppercase tracking-wider text-xs">Amount</th>
                                                                    <th className="px-6 py-3 font-medium uppercase tracking-wider text-xs">Merchant</th>
                                                                    <th className="px-6 py-3 font-medium uppercase tracking-wider text-xs">Description</th>
                                                                    <th className="px-6 py-3 font-medium uppercase tracking-wider text-xs">Category</th>
                                                                    <th className="px-6 py-3 font-medium uppercase tracking-wider text-xs">Attachment</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y divide-gray-100 dark:divide-zinc-800/60">
                                                                    {group.report.receipts.map(receipt => (
                                                                    <tr key={receipt.id} className="hover:bg-white dark:hover:bg-zinc-800 transition-colors bg-white/50 dark:bg-zinc-900/50">
                                                                        <td className="px-6 py-3 whitespace-nowrap text-gray-500 dark:text-gray-400 text-xs font-medium tracking-wide">{formatReimbursementDate(new Date(receipt.transaction_date ?? receipt.created_at))}</td>
                                                                        <td className="px-6 py-3 font-mono font-medium text-gray-900 dark:text-gray-100">{Number(receipt.amount).toFixed(2)} <span className="text-gray-500 text-xs ml-0.5">{receipt.currency}</span></td>
                                                                        <td className="px-6 py-3 font-medium text-gray-900 dark:text-gray-100">{trimStringLength(receipt.vendor_name)}</td>
                                                                        <td className="px-6 py-3 text-gray-500 truncate max-w-[300px]">{receipt.user_description}</td>
                                                                        <td className="px-6 py-4 truncate max-w-[200px] text-gray-500">{receipt.maris_category_id !== null ? GlobalVariables.accountingCategoriesByID[receipt.maris_category_id] : 'Unknown'}</td>
                                                                        <td className="px-6 py-3">
                                                                        {receipt.supabase_image_link ? (
                                                                            <button onClick={(e) => { e.stopPropagation(); setViewingReceipt(receipt); setIsViewerOpen(true); }} className="p-1.5 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-full text-gray-500 transition-colors bg-white dark:bg-zinc-900 shadow-sm border border-gray-200 dark:border-zinc-800">
                                                                                <Paperclip className="w-3.5 h-3.5" />
                                                                            </button>
                                                                        ) : <span className="text-gray-300 dark:text-gray-600 ml-2">-</span>}
                                                                        </td>
                                                                    </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                    {/* )} */}
                </div>
                </div>

                {/* Create / Edit Modal (Standard User) */}
                <Dialog open={isReceiptModalOpen} onOpenChange={setIsReceiptModalOpen}>
                    <DialogContent className="sm:max-w-[500px] overflow-hidden">
                        <DialogHeader className="bg-gray-50/50 dark:bg-zinc-900/50 p-6 pb-4 border-b border-gray-100 dark:border-zinc-800/50">
                        <DialogTitle className="text-xl">{editingReceipt?.id ? 'Edit Receipt' : 'New Receipt'}</DialogTitle>
                        <DialogDescription className="mt-2">
                            Submit your expense details. Attachments are recommended for faster approval.
                        </DialogDescription>
                        </DialogHeader>
                        <div className="p-6 pt-2">
                        <ReceiptForm 
                            initialData={editingReceipt} 
                            onSave={() => {}}
                            //ToDO: onSave={(data) => saveReceiptMutation.mutate(data)} 
                            onCancel={() => setIsReceiptModalOpen(false)}
                        />
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Attachment Viewer Modal */}
                <Dialog open={isViewerOpen} onOpenChange={setIsViewerOpen}>
                    <DialogContent className="sm:max-w-[800px] p-0 overflow-hidden bg-zinc-950 text-zinc-100 border-zinc-800 shadow-2xl">
                        {viewingReceipt && (
                            <div className="flex flex-col md:flex-row h-[600px]">
                                <div className="flex-1 bg-black flex items-center justify-center p-4 relative group">
                                {SignedImageURL ? (
                                //    <img src={viewingReceipt.supabase_image_link} alt="Receipt" className="max-w-full max-h-full object-contain rounded-md" />
                                    <ReceiptPreview
                                        className="w-[450px] max-w-[450px]"
                                        src={SignedImageURL !== '' ?  SignedImageURL : GlobalConstants.receiptPlaceholder}
                                        alt={viewingReceipt.user_description ?? ''}
                                        type={documentType}
                                        description={trimStringLength(viewingReceipt.general_item_description, 75)}
                                        allowDownload={ReduxUpdater.authenticatedUser?.user_role === GlobalConstants.UserRole.ROOT}
                                        cc={viewingReceipt.pan_number}
                                    />
                                ) : (
                                    <div className="text-zinc-600 flex flex-col items-center gap-3">
                                    <AlertTriangle className="w-12 h-12 opacity-50" />
                                    <p className="text-lg">No image attached</p>
                                    </div>
                                )}
                                </div>
                                <div className="w-full md:w-80 bg-zinc-900 p-8 flex flex-col gap-8 border-l border-zinc-800 overflow-y-auto">
                                <div className="space-y-1">
                                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest">Merchant</p>
                                    <p className="font-medium text-xl tracking-tight">{viewingReceipt.vendor_name}</p>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest">Amount</p>
                                    <p className="font-mono text-3xl font-medium text-white">{Number(viewingReceipt.amount).toFixed(2)} <span className="text-lg text-zinc-400 font-sans">{viewingReceipt.currency}</span></p>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest">Date</p>
                                    <p className="text-lg">{viewingReceipt.transaction_date}</p>
                                </div>
                                <div className="space-y-2 flex-1">
                                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest">Description</p>
                                    <p className="text-sm text-zinc-300 leading-relaxed bg-zinc-950/50 p-4 rounded-lg border border-zinc-800/50">{viewingReceipt.user_description}</p>
                                </div>
                                </div>
                            </div>
                        )}
                        {viewingReport && (
                            <div className="flex flex-col md:flex-row h-[800px]">
                                <div className="flex-1 bg-black flex items-center justify-center p-4 relative group">
                                    {SignedImageURL && 
                                        <ReceiptPreview
                                            className="w-[550px] max-w-[550px]"
                                            src={SignedImageURL !== '' ?  SignedImageURL : GlobalConstants.receiptPlaceholder}
                                            alt={viewingReport.package_id ?? ''}
                                            type={documentType}
                                            description={null}
                                            allowDownload={true}
                                            cc={viewingReceipt?.pan_number}
                                        />
                                    }
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>

                {/* Confirm Reimbursement Event Modal */}
                <Dialog open={isConfirmReimburseOpen} onOpenChange={setIsConfirmReimburseOpen}>
                    <DialogContent className="sm:max-w-[450px] bg-white">
                        <DialogHeader>
                            <DialogTitle className="text-red-600 flex items-center gap-2 text-xl">
                                <AlertTriangle className="w-6 h-6" /> Initiate Reimbursement
                            </DialogTitle>
                        </DialogHeader>
                        <div className="py-4">
                            <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                                You are about to process reimbursements for <strong className="text-gray-900 dark:text-white px-1.5 py-0.5 bg-gray-100 dark:bg-zinc-800 rounded">{eligibleForReimbursement.length}</strong> eligible receipts belonging to <strong className="text-gray-900 dark:text-white px-1.5 py-0.5 bg-gray-100 dark:bg-zinc-800 rounded">{currentSelectedUserState?.first_name} {currentSelectedUserState?.last_name}{/*role === 'manager' ? selectedUserFilter : currentSelectedUserState?.id*/}</strong>. 
                            </p>
                            <div className="mt-4 p-4 bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/30 rounded-lg">
                                <p className="text-sm text-red-800 dark:text-red-300 font-medium">
                                    This action is final and cannot be undone.
                                </p>
                                {/* <ul className="mt-2 text-sm text-red-700/80 dark:text-red-400/80 list-disc list-inside space-y-1">
                                    <li>Approved and unreviewed receipts from previous months will be Reimbursed.</li>
                                    <li>Denied receipts from previous months will be trashed.</li>
                                </ul> */}
                            </div>
                        </div>
                        <DialogFooter className="mt-2 gap-2 sm:gap-0">
                            <Button variant="outline" onClick={() => setIsConfirmReimburseOpen(false)}>Cancel</Button>
                            <Button variant="destructive" onClick={() => initiateReimbursementMutation.mutate()} disabled={initiateReimbursementMutation.isPending} className="shadow-sm">
                                {initiateReimbursementMutation.isPending ? 'Processing...' : 'Confirm & Process'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </>
    );
}

// --- Subcomponents ---

function ReceiptForm({ initialData, onSave, onCancel }: { initialData: any, onSave: (d: any) => void, onCancel: () => void }) {
  const [formData, setFormData] = useState({
    merchant: initialData?.merchant || '',
    date: initialData?.date || format(new Date(), 'yyyy-MM-dd'),
    amount: initialData?.amount || '',
    currency: initialData?.currency || 'USD',
    description: initialData?.description || '',
    image_url: initialData?.image_url || ''
  });

  const [missingImageWarning, setMissingImageWarning] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.image_url && !missingImageWarning) {
      setMissingImageWarning(true);
      return;
    }
    onSave({ ...initialData, ...formData });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 pt-2">
      {missingImageWarning && (
        <div className="bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200 p-4 rounded-lg text-sm border border-amber-200 dark:border-amber-900/50 flex items-start gap-3 shadow-sm">
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="leading-relaxed">You haven't attached a receipt image. Are you sure you want to submit? Click <strong>Save Receipt</strong> again to proceed without an image.</p>
        </div>
      )}
      
      <div className="grid grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Merchant *</Label>
          <Input required value={formData.merchant} onChange={e => setFormData(d => ({...d, merchant: e.target.value}))} className="shadow-sm" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Date *</Label>
          <Input type="date" required value={formData.date} onChange={e => setFormData(d => ({...d, date: e.target.value}))} className="shadow-sm" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Amount *</Label>
          <Input type="number" step="0.01" min="0" required value={formData.amount} onChange={e => setFormData(d => ({...d, amount: e.target.value}))} className="shadow-sm font-mono" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Currency *</Label>
          <Select value={formData.currency} onValueChange={v => setFormData(d => ({...d, currency: v}))}>
            <SelectTrigger className="shadow-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USD">USD ($)</SelectItem>
              <SelectItem value="EUR">EUR (€)</SelectItem>
              <SelectItem value="GBP">GBP (£)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Description *</Label>
        <Textarea required value={formData.description} onChange={e => setFormData(d => ({...d, description: e.target.value}))} className="shadow-sm resize-none" rows={3} />
      </div>

      <div className="space-y-2 relative border-2 border-dashed border-gray-200 dark:border-zinc-700 rounded-xl p-8 flex flex-col items-center justify-center text-center bg-gray-50/50 dark:bg-zinc-900/50 hover:bg-gray-50 hover:border-primary/50 transition-all cursor-pointer group">
        <input 
          type="file" 
          accept="image/*" 
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          disabled={isUploading}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            
            setIsUploading(true);
            try {
              const fileExt = file.name.split('.').pop();
              const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
              const filePath = `${fileName}`;
              
              const { error: uploadError } = await supabase.storage
                .from('receipts')
                .upload(filePath, file);

              if (uploadError) {
                throw uploadError;
              }

              const { data } = supabase.storage
                .from('receipts')
                .getPublicUrl(filePath);

              setFormData(d => ({...d, image_url: data.publicUrl}));
              setMissingImageWarning(false);
            } catch (error) {
              console.error('Error uploading image: ', error);
              alert('Error uploading image');
            } finally {
              setIsUploading(false);
            }
          }}
        />
        {formData.image_url ? (
           <div className="flex flex-col items-center gap-3">
             <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
               <Check className="w-6 h-6 text-green-600 dark:text-green-400" />
             </div>
             <span className="text-sm font-medium text-green-700 dark:text-green-400">Image Attached Successfully</span>
             <span className="text-xs text-gray-500 hover:text-primary transition-colors">Click to replace image</span>
           </div>
        ) : (
          <>
            <div className="w-12 h-12 bg-white dark:bg-zinc-800 rounded-full flex items-center justify-center shadow-sm border border-gray-100 dark:border-zinc-700 mb-3 group-hover:scale-105 transition-transform">
              <Upload className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" />
            </div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{isUploading ? "Uploading..." : "Click to upload receipt image"}</p>
            <p className="text-xs text-gray-500 mt-1.5">PNG, JPG up to 5MB</p>
          </>
        )}
      </div>

      <DialogFooter className="pt-6 border-t border-gray-100 dark:border-zinc-800 mt-6">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="shadow-sm">Save Receipt</Button>
      </DialogFooter>
    </form>
  );
}